<template>
    <AdminPanelTemplate>
        <template #breadcrumbs>
            <Breadcrumbs :items="[{ title: 'Properties', to: useRoute().build(Property.routes().index) }, { title: 'Property Details' }]" />
        </template>
        <template #content>
            <!-- Header -->
            <v-card variant="text">
                <v-card-title>{{ page.props.data.property_name }}</v-card-title>
                <v-card-subtitle>{{ page.props.data.property_code }}</v-card-subtitle>
            </v-card>

            <v-alert v-if="page.props.data.inventory_generation_paused" icon="mdi-information" icon-size="24" color="warning" variant="tonal" class="ma-3"> Inventory generation is paused for this property. Resume it under Stewardship &amp; Inventories. </v-alert>

            <!-- Section 1: Property Details (identification + location) -->
            <v-card class="ma-3">
                <v-card-title class="d-flex align-center justify-space-between">
                    Property Details
                    <GenericActionDialog confirm-btn-text="Update" title="Edit Property" :disabled="processing" :loading="processing" @confirm="confirm($event)">
                        <template #activator="{ props: activatorProps }">
                            <v-btn icon="mdi-pencil" variant="text" size="small" v-bind="activatorProps" :loading="processing" />
                        </template>
                        <template #body>
                            <UpdatePropertyDialog :item="page.props.data" />
                        </template>
                    </GenericActionDialog>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" sm="12">
                            <ReadOnlyTextField :value="page.props.data.property_name" label="Property Name" />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <ReadOnlyTextField :value="page.props.data.community" label="Community" />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <ReadOnlyTextField :value="page.props.data.municipality" label="Municipality" />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <ReadOnlyTextField :value="page.props.data.state.data._3_letter_code" label="State" />
                        </v-col>
                        <v-col cols="12" sm="12">
                            <ReadOnlyTextField :value="page.props.data.owner.data.name" label="Owner" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <ReadOnlyDateTextField :date="page.props.data.start_date" label="Start Date" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <ReadOnlyDateTextField :date="page.props.data.end_date" label="End Date" />
                        </v-col>
                        <v-col cols="12">
                            <Autocomplete v-model:selected-items="projectsData" field-name="name" :items="projectsData" label="Project(s)" active multiple readonly />
                        </v-col>
                    </v-row>
                </v-card-text>

                <!-- Section 2: Stewardship & Inventories -->
                <v-card-title>Stewardship &amp; Inventories</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" sm="12">
                            <ReadOnlyTextField :value="page.props.data.steward.data.steward_code" label="Steward" />
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <ReadOnlyTextField :value="page.props.data.inventory_type.data.name" label="Inventory Type" />
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <ReadOnlyTextField :value="formatNumber(page.props.data.priority, 2)" label="Queue Priority" />
                        </v-col>
                        <v-col cols="12">
                            <PropertyInventoryPauseSwitch :property="page.props.data" />
                        </v-col>
                    </v-row>
                </v-card-text>

                <!-- Section 3: Buffer Parameters -->
                <v-card-title>Buffer Parameters</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <ReadOnlyTextField :value="page.props.data.buffer" label="Buffer %" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <ReadOnlyTextField :value="page.props.data.carbon_loss" label="Carbon Loss (tCO2e)" />
                        </v-col>
                        <v-col cols="12" sm="12">
                            <ReadOnlyTextField :value="page.props.data.additional_info" label="Carbon Loss Comments" />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Section 2: Property Partitions (Version-aware) -->
            <v-card class="ma-3">
                <v-card-title class="d-flex align-center justify-space-between">
                    <span>Property Partitions</span>
                    <div class="d-flex align-center ga-3">
                        <!-- Version Dropdown -->
                        <v-select v-if="versions.length > 0" v-model="selectedVersionId" :items="versionDropdownItems" item-title="label" item-value="id" hide-details variant="plain" />
                        <!-- Action Buttons -->
                        <GenericActionDialog v-if="selectedVersion?.is_editable" confirm-btn-text="Save" title="Edit Partitions" :disabled="processing" :loading="processing" @confirm="confirm($event)">
                            <template #activator="{ props: activatorProps }">
                                <v-btn color="primary" v-bind="activatorProps">Edit Partitions</v-btn>
                            </template>
                            <template #body>
                                <UpdatePropertyVersionDialog :item="selectedVersion!" :property="{ data: page.props.data } as unknown as SingleObjectResponse<IProperty>" />
                            </template>
                        </GenericActionDialog>
                        <GenericActionDialog v-else-if="selectedVersion && !selectedVersion.is_editable && selectedVersion.status === VersionStatus.Active" confirm-btn-text="Save" title="Add New Version" :disabled="processing" :loading="processing" @confirm="confirm($event)">
                            <template #activator="{ props: activatorProps }">
                                <v-btn color="primary" v-bind="activatorProps">Add New Version</v-btn>
                            </template>
                            <template #body>
                                <CreatePropertyVersionDialog :item="{} as IPropertyVersion" :property="{ data: page.props.data } as unknown as SingleObjectResponse<IProperty>" />
                            </template>
                        </GenericActionDialog>
                    </div>
                </v-card-title>

                <!-- State banners -->
                <v-alert v-if="selectedVersion?.status === VersionStatus.Archived" type="info" variant="tonal" class="ma-3"> You are viewing a historical version. All fields are read-only except Reason for Change. </v-alert>
                <v-alert v-else-if="selectedVersion?.status === VersionStatus.Active && selectedVersion?.is_editable" type="success" variant="tonal" class="ma-3"> This version is not linked to an inventory yet and can be freely edited. </v-alert>
                <v-alert v-else-if="selectedVersion?.status === VersionStatus.Active && !selectedVersion?.is_editable" type="warning" variant="tonal" class="ma-3"> This version is linked to an inventory and cannot be edited. To make any changes, add a new version. </v-alert>

                <v-card-text v-if="selectedVersion">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <ReadOnlyTextField :value="selectedVersion.years_display" label="Years of Application" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <ReadOnlyTextField :value="formatArea(selectedVersion.property_area_ha)" label="Total Area (ha)" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <ReadOnlyTextField :value="formatArea(selectedVersion.partitioned_area)" label="Partitioned Area (ha)" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <ReadOnlyTextField :value="formatArea(selectedVersion.unpartitioned_area)" label="Unpartitioned Area (ha)" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <div v-if="selectedVersion.status === VersionStatus.Archived" class="d-flex align-start ga-2">
                                    <ReadOnlyTextField :value="selectedVersion.reason_for_change" label="Reason for Change" class="flex-grow-1" />
                                    <GenericActionDialog confirm-btn-text="Save" title="Edit Reason for Change" :disabled="processing" :loading="processing" @confirm="confirm($event)">
                                        <template #activator="{ props: activatorProps }">
                                            <v-btn icon="mdi-pencil" variant="text" size="small" v-bind="activatorProps" :loading="processing" />
                                        </template>
                                        <template #body>
                                            <UpdateVersionReasonDialog :item="selectedVersion!" :property-id="page.props.data.id" />
                                        </template>
                                    </GenericActionDialog>
                                </div>
                                <ReadOnlyTextField v-else :value="selectedVersion.reason_for_change" label="Reason for Change" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <div class="text-caption text-medium-emphasis mb-1">Partition Status</div>
                                <v-chip :color="Property.getStatusColor(selectedVersion.partition_status)" size="small">
                                    {{ selectedVersion.partition_status }}
                                </v-chip>
                            </v-col>
                        </v-row>

                        <!-- Partition rows -->
                        <v-divider class="my-4" />
                        <v-table v-if="selectedVersion.version_partitions?.data?.length > 0">
                            <thead>
                                <tr>
                                    <th>Area (ha)</th>
                                    <th>Vegetation Name</th>
                                    <th>Vegetation Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="partition in selectedVersion.version_partitions.data" :key="partition.id">
                                    <td>{{ formatArea(partition.partition_area_ha) }}</td>
                                    <td>{{ partition.landuse_type?.data?.name }}</td>
                                    <td>{{ partition.landuse_type?.data?.landuse_code }}</td>
                                </tr>
                            </tbody>
                        </v-table>
                        <p v-else class="text-grey">No partitions defined for this version.</p>
                    </v-container>
                </v-card-text>
            </v-card>
        </template>
    </AdminPanelTemplate>
</template>

<script setup lang="ts">
import { type IProperty, Property } from '@app/land-hold/property/models/Property';
import type { SingleObjectResponse } from '@shared/types/ApiResponse';
import { VersionStatus } from '@app/land-hold/property/models/PropertyVersion';
import type { IPropertyVersion } from '@app/land-hold/property/models/PropertyVersion';
import { useVersionSelector } from '@app/land-hold/property/composables/useVersionSelector';
import { useSubmitForm } from '@shared/composables/data-table/useSubmitForm';
import { useRoute } from '@shared/composables/useRoute';
import { formatArea } from '@shared/utils/formatArea';
import { formatNumber } from '@shared/utils/formatNumber';

const page = Property.routes().show.page;
const projectsData = computed(() => page.props.data.projects.data ?? []);

const { confirm, processing } = useSubmitForm();

// Version management
const versions = computed<IPropertyVersion[]>(() => page.props.data.versions?.data ?? []);
const { selectedVersionId, selectedVersion, versionDropdownItems } = useVersionSelector(versions);
</script>
